import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VmeCommonModule } from '../../vme-common.module';
import { IFuelPriceAdjusterSetting } from '../../interfaces/fuel-price-adjuster-setting';
import { VmePipesModule } from '../../vme-pipes.module';
import {
  AbstractControl,
  FormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FuelPriceAdjusterService } from '../../services/fuel-price-adjuster-service';
import { VmeDirectivesModule } from '../../vme-directives.module';
import { IFuelPriceAdjusterPrice } from '../../interfaces/fuel-price-adjuster-price';
import { CalendarModule, MessageService, SelectItem } from 'primeng';
import { Message } from '../../classes/Message';
import { IFuelPriceAdjusterBanding } from '../../interfaces/fuel-price-adjuster-banding';
import moment from 'moment';
import { OrganisationsService } from '../../services';

@Component({
  selector: 'app-fuel-price-adjuster',
  standalone: true,
  imports: [CommonModule, VmeCommonModule, VmePipesModule, VmeDirectivesModule, CalendarModule],
  templateUrl: './fuel-price-adjuster.component.html',
})
export class FuelPriceAdjusterComponent implements OnInit {
  petrolFuelType = 'Petrol';
  fuelPriceAdjusterSettings: IFuelPriceAdjusterSetting[] = [];
  fuelPriceAdjusterPrices: IFuelPriceAdjusterPrice[];
  petrolPositiveBandings: IFuelPriceAdjusterBanding[];
  petrolNegativeBandings: IFuelPriceAdjusterBanding[];
  dieselPositiveBandings: IFuelPriceAdjusterBanding[];
  dieselNegativeBandings: IFuelPriceAdjusterBanding[];
  excludedCustomers: string[] = [];
  customersOptions: SelectItem[] = [];
  selectedCustomerOptions: string[] = [];
  excludedCustomerIds: string[] = [];

  addFuelForm: UntypedFormGroup;
  showAddFuelPricesDialog: boolean;
  loadingSettings = false;
  loading = false;
  isEnabled: boolean;
  fuelPriceAdjusterCurrentPrice: IFuelPriceAdjusterPrice;
  tab = 0;
  maxDate = new Date();
  showExcludeCustomersDialog = false;

  constructor(
    private fuelPriceAdjusterService: FuelPriceAdjusterService,
    private messageService: MessageService,
    private organisationService: OrganisationsService,
  ) {}

  async ngOnInit() {
    this.initForm();
    await this.loadFuelPriceAdjusterSettings();
    await this.loadFuelPriceAdjusterPricesAndStatus();
  }

  initForm(): void {
    this.addFuelForm = new UntypedFormGroup({
      petrolPrice: new FormControl('', [Validators.required, this.decimalFormatValidator()]),
      dieselPrice: new FormControl('', [Validators.required, this.decimalFormatValidator()]),
      effectiveDate: new FormControl('', Validators.required),
    });
  }

  async loadFuelPriceAdjusterSettings() {
    this.loadingSettings = true;

    try {
      const [settings, customerIdString, customerOptions] = await Promise.all([
        this.fuelPriceAdjusterService.getSettings(),
        this.fuelPriceAdjusterService.getDisabledCustomerIds(),
        this.organisationService.getAllCustomersLookup(),
      ]);

      if (settings) {
        this.fuelPriceAdjusterSettings = settings;
        this.fuelPriceAdjusterSettings.forEach((setting) => {
          this.createBandings(setting);
        });
      }

      this.customersOptions = customerOptions;
      const customerIds = new Set(customerIdString?.split(','));

      if (customerIds.size > 0) {
        this.customersOptions.forEach((o) => {
          if (customerIds.has(String(o.value))) {
            this.excludedCustomerIds.push(o.value);
            this.excludedCustomers.push(o.label);
          }
        });
      }

      this.selectedCustomerOptions = [...this.excludedCustomerIds];
    } catch (error) {
      this.messageService.add(
        Message.Error('Failed to load fuel price adjuster settings', 'Error'),
      );
    } finally {
      this.loadingSettings = false;
    }
  }

  private createBandings(setting: IFuelPriceAdjusterSetting): any {
    const positiveBandings: IFuelPriceAdjusterBanding[] = [];
    const negativeBandings: IFuelPriceAdjusterBanding[] = [];

    for (let i = 1; i <= 12; i++) {
      const positiveBanding: IFuelPriceAdjusterBanding = {
        band: i,
        price: setting.upperThreshold + (i - 1) * setting.bandingIncrement,
        surcharge: i * setting.surchargeBandingCoefficient + setting.baseSurcharge,
      };

      const negativeBanding: IFuelPriceAdjusterBanding = {
        band: i,
        price: setting.lowerThreshold - (i - 1) * setting.bandingIncrement,
        surcharge: -i * setting.surchargeBandingCoefficient - setting.baseSurcharge,
      };

      positiveBandings.push(positiveBanding);
      negativeBandings.push(negativeBanding);
    }

    if (setting.fuelType == this.petrolFuelType) {
      this.petrolPositiveBandings = positiveBandings;
      this.petrolNegativeBandings = negativeBandings;
    } else {
      this.dieselPositiveBandings = positiveBandings;
      this.dieselNegativeBandings = negativeBandings;
    }
  }

  async loadFuelPriceAdjusterPricesAndStatus() {
    this.loading = true;

    try {
      const [prices, isEnabled] = await Promise.all([
        this.fuelPriceAdjusterService.getPrices(),
        this.fuelPriceAdjusterService.getStatus(),
      ]);

      if (prices) {
        this.fuelPriceAdjusterPrices = prices;
        this.fuelPriceAdjusterCurrentPrice = this.getClosestDate(prices);
      }

      this.isEnabled = isEnabled;
    } catch (e) {
      this.messageService.add(
        Message.Error('Failed to load fuel price adjuster prices and status', 'Error'),
      );
    } finally {
      this.loading = false;
    }
  }

  openAddFuelPricesDialog(): void {
    this.addFuelForm.patchValue({
      petrolPrice: this.fuelPriceAdjusterCurrentPrice?.petrolPrice,
      dieselPrice: this.fuelPriceAdjusterCurrentPrice?.dieselPrice,
      effectiveDate: new Date(),
    });

    this.showAddFuelPricesDialog = true;
  }

  async saveFuelPrices() {
    const formValue = this.addFuelForm.value;

    const payload = {
      dieselPrice: formValue.dieselPrice,
      petrolPrice: formValue.petrolPrice,
      effectiveDate: moment(formValue.effectiveDate).local().format(),
    };

    await this.fuelPriceAdjusterService.updateFuelPrices(payload);

    this.showAddFuelPricesDialog = false;
    await this.loadFuelPriceAdjusterPricesAndStatus();
  }

  async changeFuelPriceAdjusterStatus() {
    try {
      await this.fuelPriceAdjusterService.updateFuelPriceAdjusterStatus();
      this.messageService.add(
        Message.Success('Fuel price adjuster status updated successfully', 'Status Updated'),
      );

      await this.loadFuelPriceAdjusterPricesAndStatus();
    } catch (e) {
      this.messageService.add(
        Message.Error('Failed to update fuel price adjuster status', 'Error'),
      );
    }
  }

  getCurrentPrice(fuelPriceAdjusterSetting: IFuelPriceAdjusterSetting): number {
    return fuelPriceAdjusterSetting?.fuelType == this.petrolFuelType
      ? this.fuelPriceAdjusterCurrentPrice?.petrolPrice
      : this.fuelPriceAdjusterCurrentPrice?.dieselPrice;
  }

  isFuelPriceAdjusterSettingTriggered(item: IFuelPriceAdjusterSetting): boolean {
    const currentPrice = this.getCurrentPrice(item);
    return currentPrice > item.upperThreshold || currentPrice < item.lowerThreshold;
  }

  getSurchargePerMile(item: IFuelPriceAdjusterSetting): number {
    if (!this.isFuelPriceAdjusterSettingTriggered(item)) {
      return 0;
    }

    const currentPrice = this.getCurrentPrice(item);
    const thresholdToUse =
      currentPrice > item.upperThreshold ? item.upperThreshold : item.lowerThreshold;

    const banding = (currentPrice - thresholdToUse) / item.bandingIncrement;

    if (banding < 0) {
      return Math.floor(banding) * item.surchargeBandingCoefficient - item.baseSurcharge;
    }

    return Math.ceil(banding) * item.surchargeBandingCoefficient + item.baseSurcharge;
  }

  changeTab($event: any) {
    this.tab = $event.index;
  }

  saveExcludedCustomers() {
    const customerIds = this.selectedCustomerOptions ? this.selectedCustomerOptions.join(',') : ' ';

    this.fuelPriceAdjusterService
      .updateFuelPriceAdjusterDisabledCustomers(customerIds)
      .then(() => {
        this.excludedCustomerIds = [...this.selectedCustomerOptions];

        this.excludedCustomers = this.customersOptions
          .filter((x) => this.selectedCustomerOptions.includes(x.value))
          .map((x) => x.label);

        this.messageService.add(
          Message.Success('Fuel price adjuster excluded customers updated successfully', 'Success'),
        );

        this.showExcludeCustomersDialog = false;
      })
      .catch((err) => {
        this.messageService.add(
          Message.Error('Failed to update fuel price adjuster excluded customers', 'Error'),
        );
      });
  }

  openExcludedCustomersDialog() {
    this.selectedCustomerOptions = [...this.excludedCustomerIds];
    this.showExcludeCustomersDialog = true;
  }

  private decimalFormatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const pattern = /^\d{3}(\.\d{1,2})?$/;

      return pattern.test(value) ? null : { decimalFormat: { value } };
    };
  }

  private getClosestDate(prices: IFuelPriceAdjusterPrice[]): IFuelPriceAdjusterPrice {
    if (!prices || prices.length === 0) {
      return null;
    }

    const todayTimestamp = new Date().getTime();

    return prices.reduce((closest, current) => {
      const currentTimestamp = new Date(current.effectiveDate).getTime();
      const closestTimestamp = new Date(closest.effectiveDate).getTime();

      const currentDiff = Math.abs(currentTimestamp - todayTimestamp);
      const closestDiff = Math.abs(closestTimestamp - todayTimestamp);

      return currentDiff < closestDiff ? current : closest;
    });
  }
}
